// iCal parsing service

import ICAL from 'ical.js';
import { format, addYears } from 'date-fns';
import { RECURRING_EVENT_YEARS } from './constants';
import { HolidayEvent } from './types';

export interface ParsedEvent {
  date: string; // YYYY-MM-DD
  title: string;
  description?: string;
}

/**
 * Parse iCal file to extract holiday events
 */
export async function parseICalFile(file: File): Promise<ParsedEvent[]> {
  const text = await file.text();
  return parseICalString(text);
}

/**
 * Parse iCal string to extract holiday events
 */
export function parseICalString(icalString: string): ParsedEvent[] {
  try {
    const jcalData = ICAL.parse(icalString);
    const comp = new ICAL.Component(jcalData);
    const vevents = comp.getAllSubcomponents('vevent');

    const events: ParsedEvent[] = [];
    const now = new Date();
    const endDate = addYears(now, RECURRING_EVENT_YEARS);

    vevents.forEach((vevent) => {
      const event = new ICAL.Event(vevent);
      const startDate = event.startDate.toJSDate();

      // Add the event
      events.push({
        date: format(startDate, 'yyyy-MM-dd'),
        title: event.summary || 'Untitled Event',
        description: event.description || undefined,
      });

      // Handle recurring events (expand for next N years)
      if (event.isRecurring()) {
        try {
          const iterator = event.iterator();
          let next = iterator.next();

          // Skip first occurrence (already added above)
          next = iterator.next();

          while (next) {
            const nextDate = next.toJSDate();
            if (nextDate > endDate) break;

            events.push({
              date: format(nextDate, 'yyyy-MM-dd'),
              title: event.summary || 'Untitled Event',
              description: event.description || undefined,
            });

            next = iterator.next();
          }
        } catch (error) {
          console.warn('Failed to expand recurring event:', error);
          // Continue with other events
        }
      }
    });

    return events;
  } catch (error) {
    throw new Error(
      `Invalid iCal format: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Extract calendar name from iCal string (X-WR-CALNAME property)
 */
export function extractCalendarName(icalString: string): string | null {
  try {
    const jcalData = ICAL.parse(icalString);
    const comp = new ICAL.Component(jcalData);
    const calNameProp = comp.getFirstProperty('x-wr-calname');

    if (calNameProp) {
      const value = calNameProp.getFirstValue();
      // Type guard - ensure it's a string
      return typeof value === 'string' ? value : null;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Convert ParsedEvent array to HolidayEvent array
 */
export function toHolidayEvents(
  events: ParsedEvent[],
  calendarId: string,
): HolidayEvent[] {
  return events.map((event) => ({
    calendarId,
    date: event.date,
    title: event.title,
    description: event.description,
  }));
}
